import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// Holds the minimal bulk-transaction state that must survive the Payment Hub
// redirect (card path reloads the page at /bulk-purchase?token=..., losing local
// component state). The receipt itself is re-fetched from the backend by reference.
interface BulkPurchaseTransactionState {
	reference: string | null;
	total: number | null;
	fee: number | null;
	prepaidCount: number | null;
	postpaidCount: number | null;
	paymentMethod: string | null;

	status: "failed" | "pending" | null;
	date: string | null;
	time: string | null;
	timestamp: number | null;
}

const initialState: BulkPurchaseTransactionState = {
	reference: null,
	total: null,
	fee: null,
	prepaidCount: null,
	postpaidCount: null,
	paymentMethod: null,

	status: null,
	date: null,
	time: null,
	timestamp: null,
};

const bulkPurchaseSlice = createSlice({
	name: "bulkPurchase",
	initialState,
	reducers: {
		setBulkTransactionDetails: (
			state,
			action: PayloadAction<{
				total: number;
				fee?: number;
				prepaidCount: number;
				postpaidCount: number;
			}>
		) => {
			state.total = action.payload.total;
			state.fee = action.payload.fee ?? null;
			state.prepaidCount = action.payload.prepaidCount;
			state.postpaidCount = action.payload.postpaidCount;
			state.timestamp = Date.now();
		},

		setBulkTransactionReference: (state, action: PayloadAction<string>) => {
			state.reference = action.payload;
			state.timestamp = Date.now();
		},

		setBulkPaymentMethod: (state, action: PayloadAction<string>) => {
			state.paymentMethod = action.payload;
			state.timestamp = Date.now();
		},

		setBulkTransactionStatus: (state, action: PayloadAction<"failed" | "pending">) => {
			state.status = action.payload;
			const now = new Date();
			state.date = now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
			state.time = now.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
			state.timestamp = Date.now();
		},

		clearBulkTransaction: () => {
			return initialState;
		},
	},
});

export const {
	setBulkTransactionDetails,
	setBulkTransactionReference,
	setBulkPaymentMethod,
	setBulkTransactionStatus,
	clearBulkTransaction,
} = bulkPurchaseSlice.actions;

export default bulkPurchaseSlice.reducer;
